import { type User, GoogleAuthProvider, getAuth, onAuthStateChanged, signInWithPopup, signInWithRedirect, signOut as fbSignOut } from 'firebase/auth';
import { type FirebaseApp, getApps, initializeApp } from 'firebase/app';
import { config } from '../config';
import { Logger } from './logger';

const log = new Logger('AuthService');

let app: FirebaseApp | undefined;
function firebaseApp(): FirebaseApp {
  app ??= getApps()[0] ?? initializeApp({ ...config.firebase, authDomain: config.firebase.authDomain || `${config.firebase.projectId}.firebaseapp.com` });
  return app;
}

export interface AuthUser {
  uid: string;
}

/** Thin wrapper over Firebase Auth so the rest of the app (and the tests) never touch the SDK. */
export interface AuthService {
  /** Calls back immediately with the current state, then on every change. Returns an unsubscribe. */
  onChange(cb: (user: AuthUser | null) => void, onError: (e: Error) => void): () => void;
  signInWithGoogle(): Promise<void>;
  signOut(): Promise<void>;
  /** Fresh (auto-refreshing) Firebase ID token for API and WebSocket auth. */
  idToken(): Promise<string>;
}

class FirebaseAuthService implements AuthService {
  private get auth() {
    return getAuth(firebaseApp());
  }

  onChange(cb: (user: AuthUser | null) => void, onError: (e: Error) => void) {
    return onAuthStateChanged(this.auth, (u: User | null) => cb(u ? { uid: u.uid } : null), onError);
  }

  async signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
      const result = await signInWithPopup(this.auth, provider);
      log.info('signed in', { uid: result.user.uid });
    } catch (e) {
      const code = (e as { code?: string }).code;
      // Installed PWAs and some in-app browsers cannot open popups; fall back to a redirect.
      if (code === 'auth/popup-blocked' || code === 'auth/operation-not-supported-in-this-environment') {
        await signInWithRedirect(this.auth, provider);
        return;
      }
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') throw new Error('Sign-in cancelled');
      throw e;
    }
  }

  async signOut() {
    await fbSignOut(this.auth);
  }

  async idToken() {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not signed in');
    return user.getIdToken();
  }
}

export let authService: AuthService = new FirebaseAuthService();
/** Test seam. */
export function setAuthService(s: AuthService) {
  authService = s;
}
